import { useEffect, useRef, useState } from "react";
import { Api } from "services/api/api.service";

interface UseCompanySubscriptionParams {
  companyId: number | null | undefined;
  isEnabled: boolean;
}

/** Fetches and returns the company's subscription name when enabled. */
export const useCompanySubscription = (
  params: UseCompanySubscriptionParams,
): string | undefined => {
  const [subscription, setSubscription] = useState<string | undefined>(
    undefined,
  );
  const [subscriptionCompanyId, setSubscriptionCompanyId] = useState<
    number | null
  >(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const companyId = params.companyId;
    if (!companyId) {
      setSubscription(undefined);
      setSubscriptionCompanyId(null);
      return;
    }
    if (!params.isEnabled) {
      setSubscription(undefined);
      setSubscriptionCompanyId(null);
      return;
    }
    if (subscriptionCompanyId === companyId) return;

    let isCancelled = false;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const loadSubscription = async (): Promise<void> => {
      try {
        const response =
          await Api().company.getCompanySubscriptionInfo(companyId);
        if (isCancelled || requestId !== requestIdRef.current) return;
        setSubscription(response.data.membershipDisplayName);
        setSubscriptionCompanyId(companyId);
      } catch {
        if (isCancelled || requestId !== requestIdRef.current) return;
        console.error("Failed to fetch company subscription");
        setSubscription(undefined);
      }
    };

    void loadSubscription();

    return () => {
      isCancelled = true;
    };
  }, [params.companyId, params.isEnabled, subscriptionCompanyId]);

  return subscription;
};
